import {useState} from "react";
import { useTranslation } from "react-i18next";
import { FaUser, FaBriefcase, FaBuilding, FaCalendarAlt, FaExclamationTriangle } from "react-icons/fa";
import { SiMaildotru } from "react-icons/si";
import { FiFileText, FiPhone, FiUser } from "react-icons/fi";
import { useRef } from "react";
import { apiService } from "../../components/Config/api.service";
import AvatarCircle from "../../components/Utils/AvatarCircle";
import { TAG_COLORS } from '../../components/Constantes/Colors';
import Modal from "../../components/Utils/ModalShow";
import { formatPhone } from "../../components/Utils/FormatUtils";
import { canEditHrData } from "../../components/Auth/permissions";

const EmployeeInfo = ({ employee, currentContract, setActiveTab, handleArchive }) => {
  const { t } = useTranslation();
  const canEdit = canEditHrData();
  const fileInputRef = useRef();
  const [modalStep, setModalStep] = useState(0); // 0: closed, 1-3: steps
  const [confirmationInput, setConfirmationInput] = useState("");
  const [payslipCleanupWarning, setPayslipCleanupWarning] = useState(null);

  const handleImageUpload = async (e) => {
    const file = fileInputRef.current.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("id", employee.id);

    try {
      await apiService.saveElement("/employee/upload_image", formData, true);
      window.location.reload(); 
    } catch (err) {
      alert(t('employeeInfo.imageUploadError'));
    }
  };
  const handleCloseModal = () => {
    setModalStep(0);
    setConfirmationInput("");
    setPayslipCleanupWarning(null);
  };

  const runArchive = async (archiveStatus, options = {}) => {
    const result = await handleArchive(archiveStatus, options);
    if (result?.needs_confirmation) {
      setPayslipCleanupWarning(result);
      setModalStep(4);
      return false;
    }
    handleCloseModal();
    return true;
  };

  const handleConfirmAction = async () => {
    const isArchiving = employee.active;
    const promptText = isArchiving ? employee.name.split(' ')[0] : "CONFIRMER";

    if (!isArchiving && modalStep === 2) {
      await runArchive(isArchiving);
      return;
    }

    if (modalStep === 3) {
      if (confirmationInput === promptText) {
        await runArchive(isArchiving);
      } else {
        alert(t('employeeInfo.confirmationFailed'));
        handleCloseModal();
      }
    } else if (modalStep === 4) {
      await runArchive(isArchiving, { confirmPayslipCleanup: true });
    } else {
      setModalStep(modalStep + 1);
    }
  };

  const isArchiving = employee.active;
  const actionText = isArchiving ? t('employeeInfo.actionArchive') : t('employeeInfo.actionUnarchive');
  const promptText = isArchiving ? employee.name.split(' ')[0] : "CONFIRMER";

  const renderModalContent = () => {
    switch (modalStep) {
      case 1:
        return {
          title: t('employeeInfo.confirmTitle', { action: actionText }),
          body: t('employeeInfo.confirmBody', { action: actionText }),
          footer: (
            <>
              <button className="btn" onClick={handleCloseModal}>{t('employeeInfo.cancel')}</button>
              <button className="btn bg-[--color-primary] text-white" onClick={handleConfirmAction}>{t('employeeInfo.yesContinue')}</button>
            </>
          )
        };
      case 2:
        return {
          title: t('employeeInfo.warningTitle'),
          body: `${t('employeeInfo.warningBodyPrefix')} ${isArchiving ?
            t('employeeInfo.warningBodyArchive')
            : t('employeeInfo.warningBodyUnarchive')}.`,
          footer: (
            <>
              <button className="btn" onClick={handleCloseModal}>{t('employeeInfo.cancel')}</button>
              <button className="btn btn-warning" onClick={handleConfirmAction}>{t('employeeInfo.understandContinue')}</button>
            </>
          )
        };
      case 3:
        return {
          title: t('employeeInfo.lastStepTitle'),
          body: (
            <div>
              <p>{t('employeeInfo.typeToConfirmPrefix')} "<span className="font-bold">{promptText}</span>" {t('employeeInfo.typeToConfirmSuffix')}</p>
              <input
                type="text"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                className="input input-bordered w-full mt-2"
                placeholder={promptText}
              />
            </div>
          ),
          footer: (
            <>
              <button className="btn" onClick={handleCloseModal}>{t('employeeInfo.cancel')}</button>
              <button
                className="btn btn-error"
                onClick={handleConfirmAction}
                disabled={confirmationInput !== promptText}
              >
                {isArchiving ? t('employeeInfo.archiveButton') : t('employeeInfo.unarchiveButton')}
              </button>
            </>
          )
        };
      case 4:
        return {
          title: t('employeesPage.pendingPayslipsTitle'),
          body: (
            <div className="space-y-3">
              <p>
                {t('employeeInfo.pendingPayslipsBody')}
              </p>
              <div className="max-h-48 overflow-y-auto rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
                {(payslipCleanupWarning?.payslips || []).map((payslip) => (
                  <div key={payslip.id} className="border-b border-orange-200 py-2 last:border-b-0">
                    <p className="font-semibold">{payslip.number || t('employeesPage.payslipFallback', { id: payslip.id })}</p>
                    <p>{payslip.date_from} - {payslip.date_to} · {payslip.state}</p>
                  </div>
                ))}
              </div>
            </div>
          ),
          footer: (
            <>
              <button className="btn" onClick={handleCloseModal}>{t('employeeInfo.cancel')}</button>
              <button className="btn btn-error" onClick={handleConfirmAction}>
                {t('employeesPage.deletePayslipsAndArchive')}
              </button>
            </>
          )
        };
      default:
        return { title: "", body: null, footer: null };
    }
  };

  const { title, body, footer } = renderModalContent();

  return (
    <div className="min-h-screen py-6 px-3 rounded-lg">
      {/* Header with profile */}
      <div className="rounded-xl mb-6">
        <div className="relative mb-4 flex items-center justify-center">
            {employee.image ? (
              <img
                src={`data:image/png;base64,${employee.image}`}
                alt="Photo profil"
                className="w-[160px] h-[160px] object-cover rounded-3xl"
              />
            ) : (
              <div className="w-[160px] h-[160px] flex items-center justify-center bg-gray-200 rounded-3xl">
                <AvatarCircle name={employee.name} size={80} />
              </div>
            )}
            {currentContract?.expiration_pending && (
              <div
                className="absolute top-2 right-2 flex items-center gap-1 bg-amber-500 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-md"
                title={t('contractExpiration.expiredTitle')}
              >
                <FaExclamationTriangle size={12} aria-hidden="true" />
                {t('employeeInfo.expiredContractBadge')}
              </div>
            )}
            <div className="absolute bottom-0 w-[160px] bg-black bg-opacity-50 rounded-b-3xl text-white text-center py-4">
              <h2 className="text-xl font-bold">{employee.name}</h2>
              <p className="text-sm">{employee.poste}</p>
            </div>
          </div>
        <div className="flex flex-col items-center">
          <div>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleImageUpload}
            />
            {canEdit && (
            <button
              className="mt-2 px-3 py-1 bg-gray-200 rounded"
              onClick={() => fileInputRef.current.click()}
            >
              {t('employeeInfo.changePhoto')}
            </button>
            )}
          </div>

          <button
            onClick={() => setActiveTab("Bulletins")}
            className="py-1 px-3 bg-[--color-primary] text-white rounded-full transition-colors flex items-center gap-2 my-3"
          >
            <FiFileText /> {t('employeeInfo.payslipPreview')}
          </button>
        </div>
      </div>

      {/* Info cards grid */}
      <div className="mb-6">
        {/* Contact card */}
        <div className="py-6 px-3">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <FiUser className="text-[--color-primary]" /> {t('employeeInfo.contactInfo')}
          </h3>

          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg bg-gray-50">
              <div>
                <p className="text-xs text-gray-500">{t('employeeInfo.phone')}</p>
                <p className="font-medium">{formatPhone(employee.phone) || "-"}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-lg bg-gray-50">
              <div>
                <p className="text-xs text-gray-500">{t('employeeInfo.email')}</p>
                <p className="font-medium break-all ">{employee.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Department card */}
        <div className="py-6 px-3">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <FaBuilding className="text-[--color-primary]" /> {t('employeeInfo.companyInfo')}
          </h3>

          <div className="space-y-2">
            <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
              <div>
                <p className="text-xs text-gray-500">{t('employeeInfo.department')}</p>
                <p className="font-medium">{employee.department_id?.name || t('employeeInfo.notAssigned')}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
              <div>
                <p className="text-xs text-gray-500">{t('employeeInfo.assignment')}</p>
                <p className="font-medium">{employee.assignment_id?.name || t('employeeInfo.notAssigned')}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
              <div>
                <p className="text-xs text-gray-500">{t('employeeInfo.manager')}</p>
                <p className="font-medium">
                  {employee.manager_name || t('employeeInfo.notAssigned')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
              <div>
                <p className="text-xs text-gray-500">{t('employeeInfo.tags')}</p>
                <p className="flex flex-wrap gap-2 mt-2">
                  {(employee.categories || []).map(cat => (
                    <span key={cat.id} 
                    style={{
                      backgroundColor: TAG_COLORS[(cat.color || 1) - 1],
                      color: "#fff"
                    }}
                    className="badge text-white px-2 py-1 rounded">
                      {cat.name}
                    </span>
                  ))}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contract card */}
      <div className="py-6 px-3">
        <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <FaBriefcase className="text-[--color-primary]" /> {t('employeeInfo.contractInfo')}
        </h3>

        <div className="gap-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
            <div>
              <p className="text-xs text-gray-500">{t('employeeInfo.contractType')}</p>
              <p className="font-medium">{currentContract ? currentContract.Ref : t('employeeInfo.noContract')}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">

            <div>
              <p className="text-xs text-gray-500">{t('employeeInfo.startDate')}</p>
              <p className="font-medium">{currentContract ? currentContract.DateStart : "-"}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">

            <div>
              <p className="text-xs text-gray-500">{t('employeeInfo.endDate')}</p>
              <p className="font-medium">{currentContract ? currentContract.DateEnd : "-"}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Archive Section */}
      {canEdit && (
      <div className="mt-auto pt-6 px-3 text-center">
          <button
            onClick={() => setModalStep(1)}
            className={`text-xs flex items-center justify-center gap-2 w-full text-center ${employee.active ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'}`}
          >
            <FaExclamationTriangle />
            {employee.active ? t('employeeInfo.archiveEmployee') : t('employeeInfo.reactivateEmployee')}
          </button>
      </div>
      )}
      <Modal
        isOpen={modalStep > 0}
        onClose={handleCloseModal}
        title={title}
        footer={footer}
      >
        {body}
      </Modal>
    </div>
  );
};

export default EmployeeInfo;
